import { applyEventFilterGroups } from '@statsbomb/kitbag-global-football/state';
import { KeysToCamelCase, EventFilter, EventMarker, EventWithRels } from '@statsbomb/parachute-types';
import { atom } from 'jotai';
import { atomWithSuspenseQuery } from 'jotai-tanstack-query';
import { filterValueAtom, filtersAtom } from '@/atoms/filters/filter';
import { fetchClientAtom } from '@/atoms/queries/client';
import { PLAY_PATTERN_API_FIELD } from '@/consts/playPatterns';
import { eventTypeOutcomes } from '@/consts/typeOutcomes';
import { EVENT_MARKERS_LIMIT } from '@/consts/visualisations';
import { eventsUrl } from '@/query/url';
import { Nullable } from '@/types/generic';
import { NestedObject } from '@/types/object';
import { convertFilterParamsToString } from '@/utils/api';
import {
  convertMarkerDataIntoMarkers,
  convertMarkerDataIntoDescriptions,
  convertEventDataIntoDescriptions,
  processEventDataAtom,
} from '@/utils/atoms/eventData';
import {
  constantFilterParamsFn,
  createFilterParams,
  FilterDefinition,
  minMaxFilterAtom,
  objectFilterParamsFn,
  plainFilterParamsFn,
  propertyFilterParamsFn,
} from '@/utils/atoms/filter';
import { createUnwrappedPitchEventsAtom } from '@/utils/atoms/vis';
import { objSnakeToCamel } from '@/utils/queries';
import { atomWithDebounce } from '../debounce';
import { minuteMinMaxFilterAtom, xgMinMaxFilterAtom } from '../filters/events';
import { competitionIdsToFilterByAtom, selectedCompetitionsAtom } from '../filters/highLevel/competitions';
import { seasonIdsToFilterByAtom } from '../filters/highLevel/seasons';
import { selectedPitchFilterCoordsAtom } from '../filters/pitchFilter';
import { pageAndOrderParamsAtom } from '../general';

export const obvSelectedAtom = minMaxFilterAtom(
  'event.attributes.obv_total_net',
  filterValueAtom('minObv'),
  filterValueAtom('maxObv'),
);

// NOTE: I don't understand why, but I can't define this as a const object. If I do that, the tests fail because the
// atom competitionIdsToFilterByAtom is undefined. If I define this as a function that returns the object, they pass,
// so that's what I've had to do ¯\_(ツ)_/¯
/** This is the definition of the filters that are used in the player data locker. */
const eventDataLockerFilterDefinition = (): FilterDefinition[] => [
  {
    apiFilterParams: objectFilterParamsFn(competitionIdsToFilterByAtom, 'event.competition_id'),
    visibleFilterParams: objectFilterParamsFn(selectedCompetitionsAtom, 'event.competition_id'),
    filterOperator: 'in',
  },
  {
    apiFilterParams: objectFilterParamsFn(seasonIdsToFilterByAtom, 'event.season_id'),
    filterOperator: 'in',
  },
  {
    apiFilterParams: objectFilterParamsFn(filterValueAtom('teamIds'), 'event.team_id'),
    filterOperator: 'in',
  },
  {
    apiFilterParams: objectFilterParamsFn(filterValueAtom('playerIds'), 'event.player_id'),
    filterOperator: 'in',
  },
  {
    apiFilterParams: objectFilterParamsFn(filterValueAtom('eventNames'), 'event.type'),
    filterOperator: 'in',
  },
  {
    apiFilterParams: objectFilterParamsFn(filterValueAtom('outcomes'), 'event.attributes.outcome'),
    filterOperator: 'in',
  },
  {
    apiFilterParams: objectFilterParamsFn(filterValueAtom('bodyParts'), 'event.attributes.body_part'),
    filterOperator: 'in',
  },
  {
    apiFilterParams: objectFilterParamsFn(filterValueAtom('playPatterns'), PLAY_PATTERN_API_FIELD),
    filterOperator: 'in',
  },
  [
    {
      apiFilterParams: objectFilterParamsFn(filterValueAtom('startDate'), 'game.date'),
      filterOperator: 'gte',
    },
    {
      apiFilterParams: objectFilterParamsFn(filterValueAtom('endDate'), 'game.date'),
      filterOperator: 'lte',
    },
  ],
  [
    {
      apiFilterParams: propertyFilterParamsFn(obvSelectedAtom, 'min'),
      filterOperator: 'gte',
    },
    {
      apiFilterParams: propertyFilterParamsFn(obvSelectedAtom, 'max'),
      filterOperator: 'lte',
    },
  ],
  {
    apiFilterParams: plainFilterParamsFn(selectedPitchFilterCoordsAtom),
    filterOperator: 'inside',
  },
  {
    apiFilterParams: constantFilterParamsFn('event.player_id', false),
    filterOperator: 'null',
  },
  { apiFilterParams: constantFilterParamsFn('event.team_id', false), filterOperator: 'null' },
  [
    {
      apiFilterParams: propertyFilterParamsFn(xgMinMaxFilterAtom, 'min'),
      filterOperator: 'gte',
    },
    {
      apiFilterParams: propertyFilterParamsFn(xgMinMaxFilterAtom, 'max'),
      filterOperator: 'lte',
    },
  ],
  {
    apiFilterParams: objectFilterParamsFn(filterValueAtom('matchPeriods'), 'event.period'),
    filterOperator: 'in',
  },
  [
    {
      apiFilterParams: propertyFilterParamsFn(minuteMinMaxFilterAtom, 'min'),
      filterOperator: 'gte',
    },
    {
      apiFilterParams: propertyFilterParamsFn(minuteMinMaxFilterAtom, 'max'),
      filterOperator: 'lte',
    },
  ],
];

export const eventDataFilterParamsAtom = atom(get => {
  const eventFilterGroups = get(filterValueAtom('eventFilterGroups'));
  const flatParams = createFilterParams(get, eventDataLockerFilterDefinition());
  return applyEventFilterGroups(flatParams, eventFilterGroups);
});

export const eventDataFilterParamsDebounceObject = atomWithDebounce<Nullable<NestedObject>>(null);
const { debouncedValueAtom: debouncedEventDataFilterParamsAtom } = eventDataFilterParamsDebounceObject;

const rawEventDataAtom = atomWithSuspenseQuery(get => {
  const { fetch } = get(fetchClientAtom);
  const queryKey = ['events', get(debouncedEventDataFilterParamsAtom), get(pageAndOrderParamsAtom)] as const;
  const queryFn = async ({ queryKey: [, filterParams, pageAndOrderParams] }: { queryKey: typeof queryKey }) => {
    if (!filterParams) return [];
    return (await fetch(eventsUrl({ ...pageAndOrderParams, ...filterParams }))) as Promise<EventWithRels[]>;
  };
  return { queryKey, queryFn };
});

export const eventDataAtom = atom(async get => processEventDataAtom(await get(rawEventDataAtom)));

const rawEventDataCountAtom = atomWithSuspenseQuery(get => {
  const { fetch } = get(fetchClientAtom);
  const queryKey = ['eventCounts', get(debouncedEventDataFilterParamsAtom)] as const;

  const queryFn = async ({ queryKey: [, filterParams] }: { queryKey: typeof queryKey }) => {
    if (!filterParams) return 0;

    return (await fetch(`/events/count${convertFilterParamsToString(filterParams)}`)) as Promise<number>;
  };

  return { queryKey, queryFn };
});

export const eventsDataCountAtom = atom(async get => (await get(rawEventDataCountAtom)).data);

const rawEventMarkersAtom = atomWithSuspenseQuery(get => {
  const { fetch } = get(fetchClientAtom);
  const queryKey = ['eventMarkers', get(debouncedEventDataFilterParamsAtom)] as const;

  const queryFn = async ({ queryKey: [, filterParams] }: { queryKey: typeof queryKey }) => {
    if (!filterParams) return [];

    return (await fetch(
      `/event-markers${convertFilterParamsToString({ ...filterParams, limit: EVENT_MARKERS_LIMIT })}`,
    )) as Promise<EventMarker[]>;
  };

  return { queryKey, queryFn };
});

export const eventMarkersAtom = atom(async get => {
  const rawEventMarkersData = await get(rawEventMarkersAtom);
  return convertMarkerDataIntoMarkers(rawEventMarkersData.data);
});

export const unwrappedEventMarkersAtom = createUnwrappedPitchEventsAtom(eventMarkersAtom);

const rawGroupedEventTypesAtom = atomWithSuspenseQuery(get => {
  const { fetch } = get(fetchClientAtom);
  const queryKey = ['groupedEventTypes'] as const;

  const queryFn = async () => (await fetch(`/event-filters`)) as Promise<EventFilter[]>;

  return { queryKey, queryFn };
});

const convertToGroupedOptions = (groupedEventTypes: KeysToCamelCase<EventFilter>[]) => {
  const options = groupedEventTypes.reduce(
    (
      acc: { label: string; options: { value: string; label: string; sortOrder: number }[] }[],
      { eventGroup, eventType, sortOrder },
    ) => {
      const group = acc.find(({ label }) => label === eventGroup);
      if (group) {
        group.options.push({ value: eventType, label: eventType, sortOrder });
      } else {
        acc.push({ label: eventGroup, options: [{ value: eventType, label: eventType, sortOrder }] });
      }
      return acc;
    },
    [],
  );

  return options.map(group => ({
    label: group.label,
    options: group.options.sort((a, b) => a.sortOrder - b.sortOrder).map(({ value, label }) => ({ value, label })),
  }));
};

export const eventNamesAtom = atom(async get => {
  const groupedEventNames = (await get(rawGroupedEventTypesAtom)).data.map(objSnakeToCamel);
  return convertToGroupedOptions(groupedEventNames);
});

export const eventOutcomesAtom = atom(get => {
  const selectedTypes = get(filtersAtom).eventNames;
  const availableOutcomes = [
    ...new Set(
      (selectedTypes.length
        ? eventTypeOutcomes.filter(({ type }) => selectedTypes.some(name => name === type))
        : eventTypeOutcomes
      ).flatMap(({ outcomes }) => outcomes),
    ),
    // we don't want to support filtering by no_outcome yet as the API currently won't run this query properly
  ].filter(outcome => outcome !== 'no_outcome');
  return availableOutcomes;
});

export const eventMarkersDescriptionsAtom = atom(async get => {
  const rawEventMarkersData = await get(rawEventMarkersAtom);
  return convertMarkerDataIntoDescriptions(rawEventMarkersData.data);
});

export const eventDataDescriptionsAtom = atom(async get => {
  const rawEventData = await get(rawEventDataAtom);
  return convertEventDataIntoDescriptions(rawEventData.data);
});
